import { Box, Stack } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { Navigate, useNavigate, useOutletContext } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  FormTextField,
  FormAutocomplete,
  PrimaryButton,
} from "../../../../../components/form";
import { createUserProfile } from "../../../../../api/profile";
import { ENUM_USER_ROLE } from "../../../../../enums";
import { ROUTE_PATHS } from "../../../../../routes/paths";
import { useAppSelector } from "../../../../../store";
import { useMutationWithDispatch } from "../../../../../hooks/useMutationWithDispatch";
import {
  moreAboutAmbassadorCompanySchema,
  MoreAboutAmbassadorCompanyFormData,
  PAGE_CONFIG,
} from "./config";
import {
  handleMoreAboutAmbassadorCompanySuccess,
  getCreateProfilePayload,
} from "./utils";
import { CreateProfileResponse, OutletContext } from "./types";
import FlexBox from "../../../../../components/layouts/Flexbox";

function MoreAboutAmbassadorCompanyForm() {
  const { setPageConfig } = useOutletContext<OutletContext>();
  const navigate = useNavigate();
  const userData = useAppSelector((state) => state.auth.user);
  const lists = useAppSelector((state) => state.lists);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<MoreAboutAmbassadorCompanyFormData>({
    resolver: zodResolver(moreAboutAmbassadorCompanySchema),
    defaultValues: {
      company: "",
      title: "",
      linkedIn: "",
    },
  });

  const { mutateAsync: submitCreateProfile, isPending: submitting } =
    useMutationWithDispatch<
      CreateProfileResponse,
      {
        company: string;
        title?: string;
        linkedIn?: string;
      }
    >((payload) => createUserProfile(payload));

  const titlesOptions = useMemo(
    () =>
      (lists?.titles?.data || []).filter((item) =>
        item?.types?.includes(ENUM_USER_ROLE.AMBASSADOR)
      ),
    [lists?.titles?.data]
  );

  useEffect(() => {
    setPageConfig({ title: PAGE_CONFIG.TITLE });
  }, [setPageConfig]);

  const onSubmit = useCallback(
    async (data: MoreAboutAmbassadorCompanyFormData) => {
      try {
        const payload = getCreateProfilePayload(data);
        const res = await submitCreateProfile(payload);

        if (res?.success) {
          handleMoreAboutAmbassadorCompanySuccess(navigate);
        }
      } catch (error) {
      }
    },
    [submitCreateProfile, navigate]
  );

  if (!localStorage.token || userData.role !== ENUM_USER_ROLE.AMBASSADOR) {
    return <Navigate to={ROUTE_PATHS.AUTH.SIGNUP} replace />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack>
        <FormTextField
          name="company"
          control={control}
          label="Company Name"
          required
          error={errors.company?.message}
        />

        <FormAutocomplete
          name="title"
          control={control}
          label="Job Title"
          options={titlesOptions}
          loading={lists?.titles?.loading}
          freeSolo
          valueField="id"
          labelField="label"
          error={errors.title?.message}
        />

        <FormTextField
          name="linkedIn"
          control={control}
          label="LinkedIn URL"
          error={errors.linkedIn?.message}
          helperText="Enter your LinkedIn profile URL (e.g., https://www.linkedin.com/in/yourprofile)"
        />

        <PrimaryButton sx={{mt: 4}} loading={submitting}>Finish</PrimaryButton>
      </Stack>
    </form>
  );
}

export default MoreAboutAmbassadorCompanyForm;
